import React, { useEffect, memo, useContext } from 'react';

import classNames from 'classnames';
import moment from 'moment';

import { isVideo } from '@shared/helpers';
import { Checkbox, CircularLoader, Icon } from '@shared/ui';

import { CardError } from './CardError';
import { Dropdown } from './Dropdown/Dropdown';
import { PlayVideoBtn } from './PlayVideoBtn/PlayVideoBtn';
import cls from './ProgressCard.module.css';
import { useProgressCard } from './useProgressCard';
import { ProgressContext } from '../../../Context/ProgressContext';

interface ProgressCardProps {
    classes?: string;
    id: string;
    onShowPrevMedia?: (
        currentIndex: number,
        currentUrl: string,
        mimeType: string,
        date?: string,
        description?: string
    ) => void;
    index: number;
    isUploadPlaceholder?: boolean;
    isUploading?: boolean;
    isTemporary?: boolean;
    previewUrl?: string;
    mimeType?: string;
    mediaUrl?: string;
    date?: string;
    description?: string;
    errorText?: string;
    onRetryUpload?: () => void;
    onCancelUpload?: () => void;
}

export const ProgressCard = memo(
    ({
        classes,
        id,
        onShowPrevMedia,
        index,
        isUploadPlaceholder = false,
        isUploading = false,
        isTemporary = false,
        previewUrl,
        mimeType,
        mediaUrl,
        date,
        description,
        errorText = '',
        onRetryUpload,
        onCancelUpload,
    }: ProgressCardProps) => {
        const { currentProgress, progress_tariff_access_edit, is_customer } = useProgressCard(id);
        const isUploadCard = isUploadPlaceholder || isUploading;
        const hasError = Boolean(errorText);

        const { isActiveMassSelectMode, onSelectedMassMediaIds, selectedMassMediaIds } =
            useContext(ProgressContext);

        if (!currentProgress && !isUploadCard && !isTemporary) {
            return null;
        }

        const resolvedMimeType = isTemporary
            ? mimeType
            : isUploadCard
            ? mimeType
            : currentProgress?.mime_type;
        const isVideoFile = isVideo(resolvedMimeType);
        const resolvedPreviewUrl = isTemporary
            ? previewUrl
            : isUploadCard
            ? previewUrl
            : currentProgress?.preview_url;
        const resolvedMediaUrl = isTemporary
            ? mediaUrl
            : isVideoFile
            ? currentProgress?.video_url
            : currentProgress?.original_url;

        return (
            <div
                className={classNames(
                    cls.ProgressCard,
                    [classes],
                    isActiveMassSelectMode && cls.massSelectedMode,
                    {
                        [cls.UploadingCard]: isUploadCard,
                    }
                )}
                onClick={(e) => {
                    e.stopPropagation();
                    if (isActiveMassSelectMode) {
                        onSelectedMassMediaIds(id);
                        return;
                    }
                    if (
                        isUploadCard ||
                        hasError ||
                        !onShowPrevMedia ||
                        !resolvedMediaUrl ||
                        !resolvedMimeType
                    ) {
                        return;
                    }
                    onShowPrevMedia(
                        index,
                        resolvedMediaUrl,
                        resolvedMimeType,
                        isTemporary ? date : currentProgress?.date,
                        isTemporary ? description : currentProgress?.description
                    );
                }}
            >
                {isActiveMassSelectMode && (
                    <div
                        className={classNames(
                            cls.checkedMode,
                            selectedMassMediaIds.includes(id) && cls.checked
                        )}
                    >
                        <Checkbox isActive={selectedMassMediaIds.includes(id)} />
                    </div>
                )}
                {hasError && <CardError textError={errorText} onRetry={onRetryUpload} />}
                {!isUploadCard && !isTemporary && !isActiveMassSelectMode && (
                    <Dropdown
                        fileId={id}
                        progress_tariff_access_edit={progress_tariff_access_edit}
                        is_customer={is_customer}
                    />
                )}
                {isUploadCard && !hasError && (
                    <>
                        <span className={cls.LoaderContainer}>
                            <CircularLoader size={73} thickness={8} />
                        </span>
                        {!isUploading && (
                            <span
                                className={cls.LoaderContainer}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onCancelUpload?.();
                                }}
                            >
                                <Icon
                                    iconName="cross"
                                    width={16}
                                    height={16}
                                    fill="white"
                                    thickness={1.5}
                                />
                            </span>
                        )}
                    </>
                )}
                {isVideoFile && !isUploadCard && !hasError && <PlayVideoBtn />}
                {!!resolvedPreviewUrl && <img src={resolvedPreviewUrl} alt="" />}
                {isUploadCard && !hasError && <div className={cls.UploadingOverlay} />}
                {!isUploadCard && !hasError && (
                    <div className={cls.description}>
                        <span>
                            {moment(isTemporary ? date : currentProgress?.date).format(
                                i18next.t('ProgressCard.description.span.format')
                            )}
                        </span>
                        <span>
                            {isTemporary
                                ? description
                                : isUploading
                                ? ''
                                : currentProgress?.description}
                        </span>
                    </div>
                )}
            </div>
        );
    }
);
